import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscribeAssignments, subscribeClients, subscribeUsers } from '../services/assignmentService';
import { subscribeQuickWorks } from '../services/quickWorkService';
import { subscribeMatters } from '../services/matterService';
import { subscribeProformas } from '../services/billingService';

const norm = (value) => String(value || '').trim().toLowerCase();
const includes = (value, term) => norm(value).includes(term);

function clientNameFor(row, clients) {
  const direct = row?.clientName || row?.client?.name || row?.client?.clientName;
  if (direct && norm(direct) !== 'client') return direct;
  const id = row?.clientId || row?.clientID || row?.client?.id || '';
  const match = clients.find((client) => String(client.id) === String(id));
  return match?.name || match?.clientName || direct || 'Client';
}

function resultRow(type, id, title, subtitle, to, searchText = '') {
  return { type, id, title, subtitle, to, searchText: norm(searchText || `${title} ${subtitle}`) };
}

export default function GlobalSearch() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const role = norm(userData?.role || 'staff');
  const userId = userData?.id || user?.uid || '';
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [quickWorks, setQuickWorks] = useState([]);
  const [matters, setMatters] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [users, setUsers] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!userId || userData?.profileType !== 'staff') return undefined;
    const unsubs = [];
    const ignore = () => {};
    unsubs.push(subscribeClients(setClients, ignore));
    unsubs.push(subscribeAssignments({ role, userId, onData: setAssignments, onError: ignore }));
    unsubs.push(subscribeQuickWorks({ role, userId, onData: setQuickWorks, onError: ignore }));
    if (role !== 'accounts') unsubs.push(subscribeMatters({ role, userId, onData: setMatters, onError: ignore }));
    if (['partner', 'accounts'].includes(role)) unsubs.push(subscribeProformas(setProformas, ignore));
    if (['admin', 'partner'].includes(role)) unsubs.push(subscribeUsers(setUsers, ignore));
    return () => unsubs.forEach((unsubscribe) => unsubscribe?.());
  }, [role, userId, userData?.profileType]);

  useEffect(() => {
    const onMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const allResults = useMemo(() => {
    const rows = [];

    clients.forEach((client) => {
      const name = client.name || client.clientName || 'Client';
      const subtitle = [client.pan && `PAN ${client.pan}`, client.gstin && `GSTIN ${client.gstin}`].filter(Boolean).join(' · ') || 'Client Master';
      rows.push(resultRow('Client', client.id, name, subtitle, `/clients?clientId=${encodeURIComponent(client.id)}`,
        [name, client.pan, client.gstin, client.email, client.mobile, client.contactPerson, client.contact, ...(client.additionalContacts || []).flatMap((c) => [c.name, c.mobile])].join(' ')));
    });

    assignments.forEach((item) => {
      const client = clientNameFor(item, clients);
      const work = item.assignmentType || item.category || item.title || 'Assignment';
      rows.push(resultRow('Assignment', item.id, `${client} — ${work}`, [item.financialYear, item.period, item.assignedToName, item.status].filter(Boolean).join(' · '), `/assignments?assignmentId=${encodeURIComponent(item.id)}`,
        [client, work, item.financialYear, item.period, item.assignedToName, item.remarks, item.status].join(' ')));
    });

    quickWorks.forEach((item) => {
      const client = clientNameFor(item, clients);
      const work = item.nature || 'Quick Work';
      rows.push(resultRow('Quick Work', item.id, `${client} — ${work}`, [item.assignedToName, item.status].filter(Boolean).join(' · '), `/quick-works?search=${encodeURIComponent(client || work)}`,
        [client, work, item.remarks, item.assignedToName, item.status, item.billingStatus].join(' ')));
    });

    matters.forEach((item) => {
      const client = clientNameFor(item, clients);
      const title = item.matterCode || item.title || item.assignmentType || 'Matter';
      rows.push(resultRow('Matter', item.id, `${client} — ${title}`, [item.lawType, item.currentStage, item.status].filter(Boolean).join(' · '), `/matters/${encodeURIComponent(item.id)}`,
        [client, title, item.noticeNumber, item.authority, item.currentStage, item.currentStatus, item.nextAction, item.assignedToName].join(' ')));
    });

    proformas.forEach((item) => {
      const client = item.clientName || item.clientSnapshot?.name || clientNameFor(item, clients);
      const number = item.proformaNumber || 'Draft proforma';
      const descriptions = (item.lineItems || []).map((line) => line.description).join(' ');
      rows.push(resultRow('Proforma', item.id, `${client} — ${number}`, [item.status, item.paymentStatus].filter(Boolean).join(' · '), `/billing?proformaId=${encodeURIComponent(item.id)}`,
        [client, number, descriptions, item.status, item.paymentStatus, item.grandTotal, item.outstanding].join(' ')));
    });

    users.forEach((person) => {
      const personRole = norm(person.role);
      if (!['staff', 'partner', 'accounts', 'admin'].includes(personRole)) return;
      rows.push(resultRow('Team', person.id, person.name || person.username || person.email || 'Team member', person.role || '', '/staff-master',
        [person.name, person.username, person.email, person.role, person.mobile, person.phone].join(' ')));
    });

    return rows;
  }, [assignments, clients, matters, proformas, quickWorks, users]);

  const results = useMemo(() => {
    const term = norm(query);
    if (term.length < 2) return [];
    return allResults
      .filter((item) => includes(item.searchText, term))
      .sort((a, b) => {
        const aStarts = norm(a.title).startsWith(term) ? 0 : 1;
        const bStarts = norm(b.title).startsWith(term) ? 0 : 1;
        return aStarts - bStarts || a.type.localeCompare(b.type) || a.title.localeCompare(b.title);
      })
      .slice(0, 12);
  }, [allResults, query]);

  const choose = (result) => {
    setOpen(false);
    setQuery('');
    navigate(result.to);
  };

  return (
    <div ref={wrapRef} className="relative hidden w-[360px] max-w-[38vw] md:block">
      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'Enter' && results[0]) choose(results[0]);
          }}
          placeholder="Search client, PAN, GSTIN, work, matter, bill…"
          className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-9 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
        />
        {query && <button type="button" onClick={() => { setQuery(''); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Clear search"><FiX /></button>}
      </div>

      {open && query.trim().length >= 2 && (
        <div className="absolute right-0 z-50 mt-2 max-h-[440px] w-[460px] max-w-[80vw] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          {results.length ? results.map((result) => (
            <button key={`${result.type}-${result.id}`} type="button" onClick={() => choose(result)} className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-slate-50">
              <span className="mt-0.5 shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">{result.type}</span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-900">{result.title}</span>
                {result.subtitle && <span className="mt-0.5 block truncate text-xs text-slate-500">{result.subtitle}</span>}
              </span>
            </button>
          )) : <div className="px-4 py-8 text-center text-sm text-slate-500">No matching records found in your permitted Pravah data.</div>}
        </div>
      )}
    </div>
  );
}
